import React, { useState, useMemo, useEffect } from "react";
import { Search, ImageOff, X, Plus } from "lucide-react";
import axios from "axios";
import toast from "react-hot-toast";

const STATUS_TABS = ["All", "Open", "Closed"];

// -------- Status color helper (blue/gray theme) --------
const statusColor = (s) => {
    switch (s) {
        case "Closed":
            return "bg-green-100 text-green-800 ring-1 ring-green-300";
        default:
            return "bg-blue-100 text-blue-800 ring-1 ring-blue-300"; // Open
    }
};

// API status ('OPEN' / 'CLOSE' / 'CLOSED') -> UI status
const mapApiStatus = (status) => {
    const s = (status || "").toUpperCase();
    if (s === "CLOSE" || s === "CLOSED") return "Closed";
    return "Open";
};

// UI status ("Open"/"Closed") -> API status ("OPEN"/"CLOSE")
const mapUiStatusToApi = (status) => {
    if (status === "Open") return "OPEN";
    if (status === "Closed") return "CLOSE";
    return "";
};

const formatDate = (iso) => {
    if (!iso) return "-";
    try {
        return new Date(iso).toLocaleString("en-IN", {
            day: "2-digit",
            month: "short",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    } catch {
        return "-";
    }
};

const WriteToHr = () => {
    const [issues, setIssues] = useState([]);
    const [issuesLoading, setIssuesLoading] = useState(true);
    const [activeResolveId, setActiveResolveId] = useState(null);
    const [resolveInputs, setResolveInputs] = useState({});
    const [resolvingId, setResolvingId] = useState(null);
    const [query, setQuery] = useState("");
    const [statusTab, setStatusTab] = useState("All");
    const [loading, setLoading] = useState(false);

    const [pageNo, setPageNo] = useState(1);
    const [pagination, setPagination] = useState({});

    // add the issue modal state
    const [isAddIssueModalOpen, setIsAddIssueModalOpen] = useState(false);
    const [issueName, setIssueName] = useState("");

    // issue detail modal (fetches the complete record by id)
    const [detailModalOpen, setDetailModalOpen] = useState(false);
    const [selectedIssue, setSelectedIssue] = useState(null);
    const [detailLoading, setDetailLoading] = useState(false);
    const [detailError, setDetailError] = useState(null);

    // search
    const [searchQuery, setSearchQuery] = useState("");
    const [debounceTimer, setDebounceTimer] = useState(null);

    // full-screen attachment viewer
    const [selectedImage, setSelectedImage] = useState(null);
    const [imageFailed, setImageFailed] = useState(false);

    const getTypeLabel = (typeIdOrLabel) => typeIdOrLabel || "Unknown";

    // Maps a raw API feedback record into what the table needs
    const mapIssue = (item) => ({
        id: item.id,
        employeeName: item.employee?.name || "-",
        employeeCode: item.employee?.employeeCode || "-",
        title: item.issue?.name || item.type || "Issue",
        description: item.body,
        typeId: item.type,
        status: mapApiStatus(item.status),
        raw: item,
    });

    const addIssue = async () => {
        setLoading(true);
        try {
            await axios.post(
                `${process.env.REACT_APP_SERVER_ADDRESS}/common/other/add-issue?issueName=${issueName}`,
                {},
                { withCredentials: true },
            );
            toast.success("Issue added successfully!");
            setIssueName("");
            setIsAddIssueModalOpen(false);
        } catch (error) {
            console.error(
                "Error adding issue:",
                error.response?.data || error.message,
            );
            toast.error(
                error.response?.data?.message || "Failed to add issue.",
            );
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (debounceTimer) clearTimeout(debounceTimer);
        const timer = setTimeout(() => {
            setSearchQuery(query);
            setPageNo(1); // reset page on new search
        }, 500);
        setDebounceTimer(timer);
        return () => clearTimeout(timer);
    }, [query]);

    const getEmployeeIssue = async (
        size = 5,
        page = 1,
        search = "",
        status = "",
    ) => {
        setIssuesLoading(true);
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_SERVER_ADDRESS}/common/other/show-feedback`,
                {
                    params: {
                        apiKey: process.env.REACT_APP_API_KEY, // TODO: confirm actual env var / query key name with backend
                        size,
                        pageNo: page,
                        search,
                        status: mapUiStatusToApi(status),
                    },
                },
            );
            console.log("issue", response?.data);
            setIssues((response?.data?.data || []).map(mapIssue));

            setPagination(response?.data?.pagination || {});
        } catch (error) {
            console.log("Error", error);
            toast.error("Unable to load issues.");
        } finally {
            setIssuesLoading(false);
        }
    };

    // Fetches the complete record for a single issue by id
    // TODO: confirm with backend whether this should be a path param
    // (/show-feedback/:id) instead of a feedbackId query param
    const fetchIssueDetails = async (id) => {
        const response = await axios.get(
            `${process.env.REACT_APP_SERVER_ADDRESS}/common/other/show-feedback`,
            {
                params: {
                    apiKey: process.env.REACT_APP_API_KEY, // TODO: confirm actual env var / query key name with backend
                    feedbackId: id,
                },
            },
        );
        return response?.data?.data;
    };

    const openIssueDetail = async (issue) => {
        setSelectedIssue(issue); // show what we already have instantly
        setDetailModalOpen(true);
        setDetailError(null);
        setDetailLoading(true);
        try {
            const fullData = await fetchIssueDetails(issue.id);
            if (fullData) {
                // detail response has no top-level `status`/mapped fields,
                // so merge onto the row we already have instead of
                // replacing it outright
                setSelectedIssue((prev) => ({
                    ...prev,
                    raw: { ...prev.raw, ...fullData },
                }));
            }
        } catch (error) {
            console.log("detail fetch error", error);
            setDetailError("Unable to load full details.");
        } finally {
            setDetailLoading(false);
        }
    };

    const closeIssueDetail = () => {
        setDetailModalOpen(false);
        setSelectedIssue(null);
        setDetailError(null);
    };

    useEffect(() => {
        getEmployeeIssue(
            5,
            pageNo,
            searchQuery,
            statusTab === "All" ? "" : statusTab,
        );
    }, [pageNo, searchQuery, statusTab]);

    // Reveal the message input for an issue's Resolve action
    const openResolveInput = (id) => {
        setActiveResolveId(id);
        setResolveInputs((prev) => ({ ...prev, [id]: "" }));
    };

    const cancelResolve = () => setActiveResolveId(null);
    const handleResolveChange = (id, value) =>
        setResolveInputs((prev) => ({ ...prev, [id]: value }));

    // Closes the issue on the backend with the written message
    const submitResolve = async (id) => {
        const message = (resolveInputs[id] || "").trim();
        if (!message) {
            toast.error("Please write a message before closing the issue.");
            return;
        }

        setResolvingId(id);
        try {
            await axios.patch(
                `${process.env.REACT_APP_SERVER_ADDRESS}/common/other/resolve-feedback`,
                { feedbackId: id, resolveMsg: message },
                { withCredentials: true },
            );
            setIssues((prev) =>
                prev.map((issue) =>
                    issue.id === id ? { ...issue, status: "Closed" } : issue,
                ),
            );
            toast.success("Issue closed successfully.");
            setActiveResolveId(null);
            setResolveInputs((prev) => ({ ...prev, [id]: "" }));
        } catch (error) {
            console.error(
                "Error resolving issue:",
                error.response?.data || error.message,
            );
            toast.error(
                error.response?.data?.message || "Failed to close issue.",
            );
        } finally {
            setResolvingId(null);
        }
    };

    const openImage = (url) => {
        setImageFailed(false);
        setSelectedImage(url);
    };
    const closeImage = () => {
        setSelectedImage(null);
        setImageFailed(false);
    };

    const filteredIssues = useMemo(() => {
        return issues.filter((issue) => {
            const matchesStatus =
                statusTab === "All" || issue.status === statusTab;
            const matchesQuery =
                !query.trim() ||
                issue.title.toLowerCase().includes(query.toLowerCase()) ||
                issue.description
                    ?.toLowerCase()
                    .includes(query.toLowerCase()) ||
                issue.employeeName
                    .toLowerCase()
                    .includes(query.toLowerCase()) ||
                issue.employeeCode.toLowerCase().includes(query.toLowerCase());
            return matchesStatus && matchesQuery;
        });
    }, [issues, statusTab, query]);

    const counts = useMemo(() => {
        const c = { All: issues.length, Open: 0, Closed: 0 };
        issues.forEach((i) => (c[i.status] = (c[i.status] || 0) + 1));
        return c;
    }, [issues]);

    const detailRaw = selectedIssue?.raw;

    return (
        <div className="min-h-screen bg-gray-50 p-6 font-sans">
            <div className="mx-auto max-w-7xl">
                {/* Header – blue underline accent */}
                <div className="mb-6 flex flex-col gap-4 border-b border-gray-200 pb-4 sm:flex-row sm:items-start sm:justify-between">
                    <div>
                        <h2 className="inline-block border-b-4 border-blue-500 pb-2 text-xl font-bold tracking-tight text-gray-900 sm:text-2xl">
                            All Issues for HR
                        </h2>
                        <p className="mt-1 text-sm text-gray-500">
                            {counts.All} total · {counts.Open} open
                        </p>
                    </div>

                    {/* Search – blue focus ring */}

                    <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                        <button
                            onClick={() => setIsAddIssueModalOpen(true)}
                            className="inline-flex w-full items-center justify-center rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 sm:w-auto"
                        >
                            <Plus size={16} className="mr-2" />
                            Add Issue
                        </button>

                        <div className="relative w-full sm:w-64">
                            <Search
                                size={16}
                                className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                            />
                            <input
                                type="text"
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                placeholder="Search employee, issue..."
                                className="w-full rounded-lg border border-gray-300 bg-white py-2 pl-9 pr-3 text-sm text-gray-900 placeholder-gray-400 shadow-sm transition focus:border-blue-500 focus:ring-2 focus:ring-blue-200 focus:outline-none"
                            />
                        </div>
                    </div>
                </div>

                {/* Status tabs – active: blue */}
                <div className="mb-6 flex gap-1 overflow-x-auto pb-1">
                    {STATUS_TABS.map((tab) => (
                        <button
                            key={tab}
                            onClick={() => setStatusTab(tab)}
                            className={`shrink-0 rounded-full px-4 py-1.5 text-sm font-medium transition-all ${
                                statusTab === tab
                                    ? "bg-blue-600 text-white shadow-sm"
                                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                            }`}
                        >
                            {tab}
                            <span className="ml-1.5 text-xs opacity-80">
                                ({counts[tab] || 0})
                            </span>
                        </button>
                    ))}
                </div>

                {issuesLoading ? (
                    <div className="flex items-center justify-center rounded-xl border border-gray-200 bg-white py-16">
                        <div className="h-6 w-6 animate-spin rounded-full border-2 border-blue-500 border-t-transparent" />
                    </div>
                ) : filteredIssues.length === 0 ? (
                    <div className="flex flex-col items-center justify-center rounded-xl border-2 border-dashed border-blue-300 bg-blue-50/30 py-16 text-center">
                        <ImageOff size={32} className="mb-2 text-blue-400" />
                        <p className="font-semibold text-gray-700">
                            No matching issues
                        </p>
                        <p className="mt-1 text-sm text-gray-500">
                            Try a different search term or status filter.
                        </p>
                    </div>
                ) : (
                    <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
                        <div className="hidden overflow-x-auto md:block">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                                            Employee
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                                            Issue
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                                            Type
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                                            Status
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                                            Details
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                                            Actions
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100 bg-white">
                                    {filteredIssues.map((issue) => {
                                        const typeLabel = getTypeLabel(
                                            issue.typeId,
                                        );
                                        const isClosed =
                                            issue.status === "Closed";
                                        const showResolveInput =
                                            activeResolveId === issue.id;

                                        return (
                                            <tr
                                                key={issue.id}
                                                className="transition hover:bg-blue-50/50"
                                            >
                                                {/* Employee */}
                                                <td className="px-4 py-4 align-top whitespace-nowrap">
                                                    <div className="text-sm font-medium text-gray-900">
                                                        {issue.employeeName}
                                                    </div>
                                                    <div className="text-xs text-gray-500">
                                                        {issue.employeeCode}
                                                    </div>
                                                </td>

                                                {/* Issue */}
                                                <td className="px-4 py-4 align-top">
                                                    <div className="font-medium text-gray-900">
                                                        {issue.title}
                                                    </div>
                                                    <div className="mt-1 line-clamp-2 text-sm text-gray-600">
                                                        {issue.description}
                                                    </div>
                                                </td>

                                                {/* Type – blue badge */}
                                                <td className="px-4 py-4 align-top">
                                                    <span className="inline-flex items-center gap-1.5 rounded-full bg-blue-50 px-3 py-1 text-xs font-medium text-blue-700 ring-1 ring-blue-200">
                                                        {typeLabel}
                                                    </span>
                                                </td>

                                                {/* Status */}
                                                <td className="px-4 py-4 align-top">
                                                    <span
                                                        className={`inline-flex rounded-full px-3 py-1 text-xs font-medium ${statusColor(issue.status)}`}
                                                    >
                                                        {issue.status}
                                                    </span>
                                                </td>

                                                {/* Details – fetches the full record by id */}
                                                <td className="px-4 py-4 align-top">
                                                    <button
                                                        onClick={() =>
                                                            openIssueDetail(
                                                                issue,
                                                            )
                                                        }
                                                        className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 transition hover:bg-gray-50"
                                                    >
                                                        View
                                                    </button>
                                                </td>

                                                {/* Actions – blue buttons */}
                                                <td className="px-4 py-4 align-top">
                                                    {isClosed ? (
                                                        <span className="inline-flex items-center text-sm font-medium text-green-600">
                                                            <span className="mr-1">
                                                                ✓
                                                            </span>{" "}
                                                            Closed
                                                        </span>
                                                    ) : showResolveInput ? (
                                                        <div className="flex flex-col gap-1.5">
                                                            <textarea
                                                                autoFocus
                                                                placeholder="Write a message..."
                                                                className="w-full rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-900 placeholder-gray-400 shadow-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none"
                                                                value={
                                                                    resolveInputs[
                                                                        issue.id
                                                                    ] || ""
                                                                }
                                                                onChange={(e) =>
                                                                    handleResolveChange(
                                                                        issue.id,
                                                                        e.target
                                                                            .value,
                                                                    )
                                                                }
                                                                disabled={
                                                                    resolvingId ===
                                                                    issue.id
                                                                }
                                                            />
                                                            <div className="flex gap-2">
                                                                <button
                                                                    onClick={() =>
                                                                        submitResolve(
                                                                            issue.id,
                                                                        )
                                                                    }
                                                                    disabled={
                                                                        resolvingId ===
                                                                        issue.id
                                                                    }
                                                                    className="flex-1 rounded-md bg-blue-600 px-3 py-1.5 text-xs font-medium text-white transition hover:bg-blue-700 disabled:opacity-60"
                                                                >
                                                                    {resolvingId ===
                                                                    issue.id
                                                                        ? "Saving..."
                                                                        : "Save"}
                                                                </button>
                                                                <button
                                                                    onClick={
                                                                        cancelResolve
                                                                    }
                                                                    disabled={
                                                                        resolvingId ===
                                                                        issue.id
                                                                    }
                                                                    className="flex-1 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 transition hover:bg-gray-50"
                                                                >
                                                                    Cancel
                                                                </button>
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <button
                                                            onClick={() =>
                                                                openResolveInput(
                                                                    issue.id,
                                                                )
                                                            }
                                                            className="w-full rounded-md bg-blue-600 px-4 py-1.5 text-xs font-medium text-white transition hover:bg-blue-700 focus:ring-2 focus:ring-blue-300 focus:outline-none"
                                                        >
                                                            Resolve
                                                        </button>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>

                        {/* Mobile card list – shown instead of the table below md */}
                        <div className="divide-y divide-gray-100 md:hidden">
                            {filteredIssues.map((issue) => {
                                const typeLabel = getTypeLabel(issue.typeId);
                                const isClosed = issue.status === "Closed";
                                const showResolveInput =
                                    activeResolveId === issue.id;

                                return (
                                    <div key={issue.id} className="p-4">
                                        <div className="flex items-start justify-between gap-3">
                                            <div className="min-w-0">
                                                <p className="truncate text-sm font-medium text-gray-900">
                                                    {issue.employeeName}
                                                </p>
                                                <p className="text-xs text-gray-500">
                                                    {issue.employeeCode}
                                                </p>
                                            </div>
                                            <span
                                                className={`inline-flex shrink-0 rounded-full px-3 py-1 text-xs font-medium ${statusColor(issue.status)}`}
                                            >
                                                {issue.status}
                                            </span>
                                        </div>

                                        <p className="mt-2 font-medium text-gray-900">
                                            {issue.title}
                                        </p>
                                        <p className="mt-1 line-clamp-2 text-sm text-gray-600">
                                            {issue.description}
                                        </p>

                                        <span className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-blue-50 px-3 py-1 text-xs font-medium text-blue-700 ring-1 ring-blue-200">
                                            {typeLabel}
                                        </span>

                                        <div className="mt-3 flex flex-col gap-2">
                                            <button
                                                onClick={() =>
                                                    openIssueDetail(issue)
                                                }
                                                className="w-full rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 transition hover:bg-gray-50"
                                            >
                                                View details
                                            </button>

                                            {isClosed ? (
                                                <span className="inline-flex items-center justify-center text-sm font-medium text-green-600">
                                                    <span className="mr-1">
                                                        ✓
                                                    </span>{" "}
                                                    Closed
                                                </span>
                                            ) : showResolveInput ? (
                                                <div className="flex flex-col gap-1.5">
                                                    <textarea
                                                        autoFocus
                                                        placeholder="Write a message..."
                                                        className="w-full rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-900 placeholder-gray-400 shadow-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none"
                                                        value={
                                                            resolveInputs[
                                                                issue.id
                                                            ] || ""
                                                        }
                                                        onChange={(e) =>
                                                            handleResolveChange(
                                                                issue.id,
                                                                e.target.value,
                                                            )
                                                        }
                                                        disabled={
                                                            resolvingId ===
                                                            issue.id
                                                        }
                                                    />
                                                    <div className="flex gap-2">
                                                        <button
                                                            onClick={() =>
                                                                submitResolve(
                                                                    issue.id,
                                                                )
                                                            }
                                                            disabled={
                                                                resolvingId ===
                                                                issue.id
                                                            }
                                                            className="flex-1 rounded-md bg-blue-600 px-3 py-1.5 text-xs font-medium text-white transition hover:bg-blue-700 disabled:opacity-60"
                                                        >
                                                            {resolvingId ===
                                                            issue.id
                                                                ? "Saving..."
                                                                : "Save"}
                                                        </button>
                                                        <button
                                                            onClick={
                                                                cancelResolve
                                                            }
                                                            disabled={
                                                                resolvingId ===
                                                                issue.id
                                                            }
                                                            className="flex-1 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 transition hover:bg-gray-50"
                                                        >
                                                            Cancel
                                                        </button>
                                                    </div>
                                                </div>
                                            ) : (
                                                <button
                                                    onClick={() =>
                                                        openResolveInput(
                                                            issue.id,
                                                        )
                                                    }
                                                    className="w-full rounded-md bg-blue-600 px-4 py-1.5 text-xs font-medium text-white transition hover:bg-blue-700 focus:ring-2 focus:ring-blue-300 focus:outline-none"
                                                >
                                                    Resolve
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        {/* Pagination */}
                        {pagination.totalPages > 1 && (
                            <div className="flex flex-col gap-2 border-t border-gray-200 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                                <span className="text-xs text-gray-500">
                                    Page {pagination.page} of{" "}
                                    {pagination.totalPages} ·{" "}
                                    {pagination.totalRecords} total
                                </span>
                                <div className="flex gap-2">
                                    <button
                                        disabled={!pagination.hasPrevPage}
                                        onClick={() =>
                                            setPageNo((p) => Math.max(1, p - 1))
                                        }
                                        className="flex-1 rounded-md border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 disabled:cursor-not-allowed disabled:opacity-40 sm:flex-none"
                                    >
                                        Previous
                                    </button>
                                    <button
                                        disabled={!pagination.hasNextPage}
                                        onClick={() => setPageNo((p) => p + 1)}
                                        className="flex-1 rounded-md border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 disabled:cursor-not-allowed disabled:opacity-40 sm:flex-none"
                                    >
                                        Next
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Issue Detail Modal – fetches the complete record by id */}
            {detailModalOpen && selectedIssue && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm"
                    onClick={closeIssueDetail}
                >
                    <div
                        className="flex max-h-[85vh] w-full max-w-lg flex-col rounded-xl bg-white shadow-xl"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Header – stays in normal flow so it can't get clipped by the scroll area below */}
                        <div className="flex items-start justify-between gap-3 border-b border-gray-100 p-4 sm:p-6">
                            <div className="min-w-0">
                                <h3 className="truncate text-lg font-semibold text-gray-900">
                                    {selectedIssue.title}
                                </h3>
                                <p className="text-sm text-gray-500">
                                    {selectedIssue.employeeName} ·{" "}
                                    {selectedIssue.employeeCode}
                                </p>
                            </div>
                            <div className="flex shrink-0 items-center gap-2">
                                <span
                                    className={`inline-flex rounded-full px-3 py-1 text-xs font-medium ${statusColor(selectedIssue.status)}`}
                                >
                                    {selectedIssue.status}
                                </span>
                                <button
                                    onClick={closeIssueDetail}
                                    className="rounded-full p-1.5 text-gray-500 transition hover:bg-gray-100 hover:text-gray-800"
                                    aria-label="Close"
                                >
                                    <X size={18} />
                                </button>
                            </div>
                        </div>

                        {/* Scrollable body */}
                        <div className="overflow-y-auto p-4 sm:p-6">
                            {detailLoading ? (
                                <div className="flex items-center justify-center py-10">
                                    <div className="h-6 w-6 animate-spin rounded-full border-2 border-blue-500 border-t-transparent" />
                                </div>
                            ) : detailError ? (
                                <p className="mt-6 text-sm text-red-600">
                                    {detailError}
                                </p>
                            ) : (
                                <div className="mt-4 space-y-4">
                                    <div>
                                        <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
                                            Description
                                        </p>
                                        <p className="mt-1 text-sm text-gray-800">
                                            {detailRaw?.body ||
                                                selectedIssue.description}
                                        </p>
                                    </div>

                                    <div>
                                        <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
                                            Raised on
                                        </p>
                                        <p className="mt-1 text-sm text-gray-800">
                                            {formatDate(detailRaw?.createdAt)}
                                        </p>
                                    </div>

                                    {detailRaw?.isReopened && (
                                        <div>
                                            <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
                                                Reopen count
                                            </p>
                                            <p className="mt-1 text-sm text-gray-800">
                                                {detailRaw.reopenCount}
                                            </p>
                                        </div>
                                    )}

                                    {detailRaw?.attachments?.length > 0 && (
                                        <div>
                                            <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
                                                Attachment
                                            </p>
                                            <div className="mt-2 flex gap-2 overflow-x-auto">
                                                {detailRaw.attachments.map(
                                                    (att) => (
                                                        <button
                                                            key={att.id}
                                                            onClick={() =>
                                                                openImage(
                                                                    att.attachment_url,
                                                                )
                                                            }
                                                            className="h-20 w-20 shrink-0 overflow-hidden rounded-lg border border-gray-200 bg-gray-50 transition hover:opacity-75"
                                                        >
                                                            <img
                                                                src={
                                                                    att.attachment_url
                                                                }
                                                                alt="Issue attachment"
                                                                className="h-full w-full object-cover"
                                                            />
                                                        </button>
                                                    ),
                                                )}
                                            </div>
                                        </div>
                                    )}

                                    {detailRaw?.trackHistory?.length > 0 && (
                                        <div>
                                            <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
                                                History
                                            </p>

                                            <div className="mt-2 space-y-2">
                                                {detailRaw.trackHistory.map(
                                                    (entry, index) => (
                                                        <div
                                                            key={`${entry.event}-${index}`}
                                                            className="border-l-2 border-blue-200 pl-3"
                                                        >
                                                            <p className="text-sm font-medium text-gray-800">
                                                                {entry.remark}
                                                            </p>

                                                            {/* Show payload only if it is a string */}
                                                            {typeof entry.payload ===
                                                                "string" &&
                                                                entry.payload.trim() && (
                                                                    <p className="mt-1 text-sm text-gray-600">
                                                                        {
                                                                            entry.payload
                                                                        }
                                                                    </p>
                                                                )}

                                                            <p className="text-xs text-gray-400">
                                                                {entry.userRole}{" "}
                                                                ·{" "}
                                                                {formatDate(
                                                                    entry.timestamp,
                                                                )}
                                                            </p>
                                                        </div>
                                                    ),
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Full-screen attachment viewer */}
            {selectedImage && (
                <div
                    className="fixed inset-0 z-[60] flex items-center justify-center bg-black/90 p-4"
                    onClick={closeImage}
                >
                    <div
                        className="relative max-h-[90vh] w-full max-w-3xl"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <button
                            onClick={closeImage}
                            className="absolute top-2 right-2 z-10 rounded-full bg-white p-1.5 text-gray-800 shadow-lg hover:bg-gray-100"
                            aria-label="Close"
                        >
                            <X size={18} />
                        </button>

                        {imageFailed ? (
                            <div className="flex h-64 w-64 flex-col items-center justify-center gap-2 rounded-lg bg-gray-800 text-gray-200">
                                <ImageOff size={28} />
                                <p className="text-sm">Image failed to load</p>
                            </div>
                        ) : (
                            <img
                                src={selectedImage}
                                alt="Full size attachment"
                                className="max-h-[90vh] w-full rounded-lg object-contain shadow-2xl"
                                onError={() => setImageFailed(true)}
                            />
                        )}
                    </div>
                </div>
            )}

            {/* add the issue modal */}
            {isAddIssueModalOpen && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm"
                    onClick={() => setIsAddIssueModalOpen(false)}
                >
                    <div
                        className="w-full max-w-md rounded-xl bg-white p-4 shadow-xl sm:p-6"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex items-center justify-between">
                            <h3 className="text-xl font-semibold">
                                Add Issue Type
                            </h3>
                            <button
                                onClick={() => setIsAddIssueModalOpen(false)}
                                className="rounded-full p-1.5 text-gray-500 transition hover:bg-gray-100 hover:text-gray-800"
                                aria-label="Close"
                            >
                                <X size={18} />
                            </button>
                        </div>

                        <input
                            type="text"
                            value={issueName}
                            onChange={(e) => setIssueName(e.target.value)}
                            className="mt-4 w-full rounded-lg border border-gray-300 p-3 focus:border-blue-500 focus:outline-none"
                            placeholder="Enter issue type..."
                        />

                        <div className="mt-4 flex justify-end">
                            <button
                                onClick={addIssue}
                                className="w-full rounded-lg bg-blue-500 px-4 py-2 text-white hover:bg-blue-600 sm:w-auto"
                                disabled={loading}
                            >
                                {loading ? "Saving..." : "Save"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default WriteToHr;
